import type { FeedingDashboardConfig } from './configTypes';
import type { FeedingPen } from './penTypes';

/**
 * The shared warning rule for the feeding dashboard (IPS 2.6.2, statements 12 and 25).
 *
 * One rule, so the pen table, the KPI row and the per-pen bar charts cannot disagree
 * about whether a value is off target. Only these four conditions put anything in the
 * warning state; other cell tones (feed vs plan, SFR, confidence interval, pellet) are
 * informational and must not use it.
 *
 * Values are compared exactly as the API delivers them, which is how they are shown:
 * bFCR rounded half-up to 2 dp (a true 1.124 shows 1.12 and does not warn against 1.12),
 * RGI floored to 1 dp (so "shown below 100" and "below 100" are the same statement).
 * Targets and margins from the settings are taken at the precision they are shown with
 * (see `warningTargetOf`), so the rule compares the same two numbers the reader sees.
 */

/** The four IPS 2.6.2 conditions, in the order the IPS lists them. */
export type PenWarningTrigger = 'fcrSystem' | 'fcrBioscope' | 'rgiSystem' | 'rgiBioscope';

/** bFCR warns above its target, RGI below. */
export type WarningMetric = 'fcr' | 'rgi';

/**
 * `warning` past the target, `critical` past the target and the configured caution
 * margin as well. Both are the warning state; the second tier grades how far off it is.
 */
export type WarningTier = 'none' | 'warning' | 'critical';

export interface WarningTarget {
    target: number;
    /** Caution margin (%) beyond the target before the tier becomes `critical`. */
    warnPct: number;
}

export interface PenWarning {
    trigger: PenWarningTrigger;
    metric: WarningMetric;
    /** The value as shown. */
    value: number;
    target: number;
    warnPct: number;
    tier: Exclude<WarningTier, 'none'>;
}

/** Decimals a metric's value and its target are shown with: bFCR 2, RGI 1. */
export const WARNING_DIGITS: Readonly<Record<WarningMetric, number>> = { fcr: 2, rgi: 1 };

/** Decimals the caution margin (%) is shown with. */
export const WARNING_MARGIN_DIGITS = 1;

function roundTo(value: number, digits: number): number {
    const scale = 10 ** digits;
    // Snapped to 12 significant digits first, so a target typed as 1.005 (stored as
    // 1.00499...) rounds to the 1.01 its author wrote.
    return Math.round(Number((value * scale).toPrecision(12))) / scale;
}

/**
 * The configured target and caution margin for a metric, from a site or a pen config, at
 * the precision they are shown with. The settings accept any decimals, and a finer target
 * than the value's own precision would contradict what the reader sees: against a target
 * of 100.04 an RGI shown as 100.0 would warn while the text named "the target 100".
 */
export function warningTargetOf(
    config: FeedingDashboardConfig,
    metric: WarningMetric
): WarningTarget {
    const configured =
        metric === 'fcr'
            ? { target: config.fcrTarget, warnPct: config.fcrWarnPct }
            : { target: config.rgiTarget, warnPct: config.rgiWarnPct };
    return {
        target: roundTo(configured.target, WARNING_DIGITS[metric]),
        warnPct: roundTo(configured.warnPct, WARNING_MARGIN_DIGITS),
    };
}

/**
 * Grades one value against an explicit target. Taking the target from the caller is what
 * lets a site-level surface (a KPI over several pens) use the site config while a pen row
 * uses the pen's own.
 */
export function warningTier(
    metric: WarningMetric,
    value: number | null,
    { target, warnPct }: WarningTarget
): WarningTier {
    if (value == null) return 'none';
    if (metric === 'fcr') {
        // Targets are validated non-negative, so a zero or negative bFCR (weight loss,
        // not overfeeding) never reaches the warning state.
        if (value <= target) return 'none';
        return value <= target * (1 + warnPct / 100) ? 'warning' : 'critical';
    }
    if (value >= target) return 'none';
    return value >= target * (1 - warnPct / 100) ? 'warning' : 'critical';
}

const TRIGGERS: {
    trigger: PenWarningTrigger;
    metric: WarningMetric;
    bioscope: boolean;
    value: (pen: FeedingPen) => number | null;
}[] = [
    { trigger: 'fcrSystem', metric: 'fcr', bioscope: false, value: (pen) => pen.fcrPms },
    { trigger: 'fcrBioscope', metric: 'fcr', bioscope: true, value: (pen) => pen.fcrBioscope },
    { trigger: 'rgiSystem', metric: 'rgi', bioscope: false, value: (pen) => pen.rgiFeed },
    { trigger: 'rgiBioscope', metric: 'rgi', bioscope: true, value: (pen) => pen.rgiBioscope },
];

/**
 * Every condition a pen meets, judged against the pen's own resolved targets so a pen at
 * a different growth stage is not flagged for meeting its own plan. Empty means the pen
 * is not in the warning state.
 *
 * Bioscope values count only on a `ready` pen, the same predicate that masks them in the
 * table: an unavailable Bioscope metric must not trigger (IPS 2.6.2), and a masked cell
 * must not carry a warning the reader cannot see the reason for.
 */
export function penWarnings(pen: FeedingPen): PenWarning[] {
    const bioscopeReady = pen.bioscopeDataQuality === 'ready';
    const warnings: PenWarning[] = [];
    for (const entry of TRIGGERS) {
        const value = entry.value(pen);
        if (value == null || (entry.bioscope && !bioscopeReady)) continue;
        const target = warningTargetOf(pen.config, entry.metric);
        const tier = warningTier(entry.metric, value, target);
        if (tier === 'none') continue;
        warnings.push({
            trigger: entry.trigger,
            metric: entry.metric,
            value,
            target: target.target,
            warnPct: target.warnPct,
            tier,
        });
    }
    return warnings;
}

/** The tier of one condition within a pen's warnings, for colouring that one cell. */
export function penWarningTier(warnings: PenWarning[], trigger: PenWarningTrigger): WarningTier {
    return warnings.find((entry) => entry.trigger === trigger)?.tier ?? 'none';
}
